import React, { useState } from 'react';
import { Terminal, Code, Globe, Copy, Check } from 'lucide-react';
import { Button } from '../../ui/Button';
import {
  SnippetVariable,
  generateCurlCode,
  generateFetchCode,
  generateHtmlFormCode,
} from '../../../utils/schemaSnippets';
import { CurlSnippet } from './CurlSnippet';
import { FetchSnippet } from './FetchSnippet';
import { HtmlFormSnippet } from './HtmlFormSnippet';

export type SnippetTab = 'curl' | 'fetch' | 'html';

export interface EndpointSnippetTabsProps {
  variables: SnippetVariable[];
  formUrl: string;
  webhookUrl: string;
  ingestToken: string;
  redirectUrl: string;
  honeypot: string;
  isSchemaDriven?: boolean;
}

export const EndpointSnippetTabs: React.FC<EndpointSnippetTabsProps> = ({
  variables,
  formUrl,
  webhookUrl,
  ingestToken,
  redirectUrl,
  honeypot,
  isSchemaDriven = false,
}) => {
  const [activeTab, setActiveTab] = useState<SnippetTab>('curl');
  const [copied, setCopied] = useState(false);

  const getActiveCode = (): string => {
    switch (activeTab) {
      case 'curl':
        return generateCurlCode(webhookUrl, ingestToken, variables);
      case 'fetch':
        return generateFetchCode(webhookUrl, ingestToken, variables);
      case 'html':
        return generateHtmlFormCode(formUrl, redirectUrl, honeypot, variables, !isSchemaDriven);
    }
  };

  const copyToClipboard = () => {
    const rawCode = getActiveCode();
    navigator.clipboard.writeText(rawCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getCopyLabel = (): string => {
    switch (activeTab) {
      case 'curl':
        return 'Copy cURL';
      case 'fetch':
        return 'Copy fetch';
      case 'html':
        return 'Copy HTML';
    }
  };

  const getTitle = (): { icon: React.ReactNode; label: string } => {
    switch (activeTab) {
      case 'curl':
        return {
          icon: <Terminal size={14} className="text-blue-400" />,
          label: 'REST API Webhook cURL Snippet',
        };
      case 'fetch':
        return {
          icon: <Code size={14} className="text-emerald-400" />,
          label: 'JavaScript Fetch Snippet',
        };
      case 'html':
        return {
          icon: <Globe size={14} className="text-cf-orange" />,
          label: 'HTML Form Snippet',
        };
    }
  };

  const current = getTitle();

  return (
    <div className="bg-cf-surface border border-cf-border rounded-lg overflow-hidden shadow-sm">
      {/* Header Bar */}
      <div className="px-3 sm:px-4 py-2 sm:py-2.5 border-b border-cf-border flex items-center justify-between gap-2 sm:gap-3 bg-cf-surface flex-wrap sm:flex-nowrap min-h-[46px]">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          {/* Tab Selector: cURL | fetch | html */}
          <div className="flex items-center bg-cf-bg p-0.5 rounded-lg border border-cf-border shrink-0 gap-0.5">
            <Button
              type="button"
              variant={activeTab === 'curl' ? 'secondary' : 'ghost'}
              size="xs"
              onClick={() => setActiveTab('curl')}
              className={activeTab === 'curl' ? 'text-blue-400 font-semibold shadow-xs' : ''}
            >
              cURL
            </Button>
            <Button
              type="button"
              variant={activeTab === 'fetch' ? 'secondary' : 'ghost'}
              size="xs"
              onClick={() => setActiveTab('fetch')}
              className={activeTab === 'fetch' ? 'text-emerald-400 font-semibold shadow-xs' : ''}
            >
              fetch
            </Button>
            <Button
              type="button"
              variant={activeTab === 'html' ? 'secondary' : 'ghost'}
              size="xs"
              onClick={() => setActiveTab('html')}
              className={activeTab === 'html' ? 'text-cf-orange font-semibold shadow-xs' : ''}
            >
              html
            </Button>
          </div>

          <div className="hidden sm:block h-4 w-px bg-cf-border shrink-0" />

          {/* Active Snippet Title */}
          <div className="hidden sm:flex items-center gap-2 min-w-0 truncate">
            <div className="shrink-0">{current.icon}</div>
            <h3 className="text-xs font-semibold text-cf-textPrimary truncate">{current.label}</h3>
          </div>
        </div>

        {/* Copy Button */}
        <Button
          type="button"
          variant="secondary"
          size="xs"
          onClick={copyToClipboard}
          leftIcon={copied ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
        >
          {copied ? (
            <span className="text-emerald-400">Copied</span>
          ) : (
            <>
              <span className="hidden sm:inline">{getCopyLabel()}</span>
              <span className="sm:hidden">Copy</span>
            </>
          )}
        </Button>
      </div>

      {/* Snippet Body */}
      <div className="h-[360px] bg-[#0d0d0d] overflow-hidden">
        {activeTab === 'curl' && (
          <CurlSnippet
            variables={variables}
            webhookUrl={webhookUrl}
            ingestToken={ingestToken}
            className="h-full"
          />
        )}
        {activeTab === 'fetch' && (
          <FetchSnippet
            variables={variables}
            webhookUrl={webhookUrl}
            ingestToken={ingestToken}
            className="h-full"
          />
        )}
        {activeTab === 'html' && (
          <HtmlFormSnippet
            variables={variables}
            formUrl={formUrl}
            redirectUrl={redirectUrl}
            honeypot={honeypot}
            isDefaultSample={!isSchemaDriven}
            className="h-full"
          />
        )}
      </div>
    </div>
  );
};
